import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';

function StatsCard({ title, value, loading }) {
    const theme = useTheme();
    const [displayValue, setDisplayValue] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const cardRef = useRef(null);

    // Intersection Observer to detect when card is in viewport
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            {
                threshold: 0.2, // Trigger when 20% of the card is visible
                rootMargin: '0px'
            }
        );

        if (cardRef.current) {
            observer.observe(cardRef.current);
        }

        return () => {
            if (cardRef.current) {
                observer.unobserve(cardRef.current);
            }
        };
    }, []);

    // Animate value when card becomes visible
    useEffect(() => {
        if (loading || !value || !isVisible) {
            setDisplayValue(0);
            return;
        }

        // Check if value is a time string or other non-numeric string
        const isNumericString = typeof value === 'string' && /^[\d,.\s%]+$/.test(value);
        
        if (!isNumericString && typeof value === 'string') {
            // For non-numeric strings like "3 h 43 min 20 sec", display directly
            setDisplayValue(value);
            return;
        }

        const targetValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '').replace(/%/g, '')) : value;
        
        if (isNaN(targetValue)) {
            setDisplayValue(value);
            return;
        }

        const duration = 2000;
        const steps = 60;
        const increment = targetValue / steps;
        const stepDuration = duration / steps;
        
        let currentStep = 0;

        const timer = setInterval(() => {
            currentStep++;
            if (currentStep >= steps) {
                setDisplayValue(targetValue);
                clearInterval(timer);
            } else {
                setDisplayValue(Math.floor(increment * currentStep));
            }
        }, stepDuration);

        return () => clearInterval(timer);
    }, [value, loading, isVisible]);

    const formatValue = (val) => {
        // If it's already a formatted string (like time), return as is
        if (typeof val === 'string' && !/^[\d,.\s%]+$/.test(val)) {
            return val;
        }
        
        if (typeof value === 'string' && value.includes('%')) {
            return `${val}%`;
        }
        if (typeof value === 'string' && value.includes(',')) {
            return val.toLocaleString();
        }
        return val;
    };

    return (
        <div 
            ref={cardRef}
            style={{
                background: theme.palette.type === 'dark'
                    ? 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)'
                    : 'linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)',
                padding: '25px',
                borderRadius: '16px',
                border: `1px solid ${theme.palette.type === 'dark' ? '#334155' : '#e0e0e0'}`,
                boxShadow: theme.palette.type === 'dark'
                    ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: loading ? 'opacity 0.6s ease, transform 0.6s ease' : 'opacity 0.6s ease, transform 0.6s ease, box-shadow 0.3s ease',
                cursor: loading ? 'default' : 'pointer'
            }}
            onMouseEnter={(e) => {
                if (!loading) {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = theme.palette.type === 'dark'
                        ? '0 12px 48px rgba(59, 130, 246, 0.3)'
                        : '0 12px 48px rgba(33, 150, 243, 0.2)';
                }
            }}
            onMouseLeave={(e) => {
                if (!loading) {
                    e.currentTarget.style.transform = isVisible ? 'translateY(0)' : 'translateY(20px)';
                    e.currentTarget.style.boxShadow = theme.palette.type === 'dark'
                        ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                        : '0 4px 20px rgba(0, 0, 0, 0.1)';
                }
            }}>
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #3b82f6, #10b981)'
            }} />
            
            {loading ? (
                <>
                    <Skeleton 
                        variant="text" 
                        width="60%" 
                        height={20}
                        style={{ marginBottom: '8px' }}
                        animation="wave"
                    />
                    <Skeleton 
                        variant="text" 
                        width="80%" 
                        height={50}
                        style={{ marginBottom: '8px' }}
                        animation="wave"
                    />
                </>
            ) : (
                <>
                    <div style={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.9rem',
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        fontWeight: 500
                    }}>
                        {title}
                    </div>
                    <div style={{
                        fontSize: '2.5rem',
                        fontWeight: 'bold',
                        marginBottom: '8px',
                        background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}>
                        {formatValue(displayValue)}
                    </div>
                </>
            )}
        </div>
    );
}

export default StatsCard;